import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { applySearchFilter } from '@/redux/modules/search';
import { setFormFilter } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { getPreservedFilters } from '@/utility/modeSwitching';

export const useSearchModes = (onModeSelect?: () => void) => {
  const dispatch = useDispatch();
  const queryParams = useSelector(getQueryParams);
  const formState = useSelector((state: TRootState) => state.searchForm.filters);

  const handleModeSelect = useCallback(
    (isSwitchingToDelivery: boolean, dryRun: boolean = false) => {
      const preservedParams = getPreservedFilters(queryParams, isSwitchingToDelivery, formState);

      if (isSwitchingToDelivery) {
        const syncedAddress =
          preservedParams[ESearchFilters.ADDRESS] ||
          preservedParams[ESearchFilters.DELIVERY_ADDRESS];
        if (syncedAddress) {
          dispatch(
            setFormFilter({
              filters: {
                [ESearchFilters.ADDRESS]:
                  typeof syncedAddress === 'string' ? syncedAddress : syncedAddress[0],
                [ESearchFilters.DELIVERY_ADDRESS]:
                  typeof syncedAddress === 'string' ? syncedAddress : syncedAddress[0],
                [ESearchFilters.DELIVERY]: true,
                [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.STATIONARY,
              },
              filterMethod: null,
            }),
          );
        }

        dispatch(
          applySearchFilter(
            {
              ...preservedParams,
              [ESearchFilters.DELIVERY]: 'true',
              [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.STATIONARY,
            },
            false,
            dryRun,
            false,
            undefined,
            true,
          ), // ignoreExistingQueryParams = true to clear all other filters
        );
      } else {
        const syncedAddress =
          preservedParams[ESearchFilters.ADDRESS] ||
          preservedParams[ESearchFilters.DELIVERY_ADDRESS];

        if (syncedAddress) {
          dispatch(
            setFormFilter({
              filters: {
                [ESearchFilters.ADDRESS]:
                  typeof syncedAddress === 'string' ? syncedAddress : syncedAddress[0],
                [ESearchFilters.DELIVERY_ADDRESS]:
                  typeof syncedAddress === 'string' ? syncedAddress : syncedAddress[0],
                [ESearchFilters.DELIVERY]: undefined,
                [ESearchFilters.DELIVERY_STATIONARY]: undefined,
              },
              filterMethod: null,
            }),
          );
        }

        dispatch(
          applySearchFilter(
            {
              ...preservedParams,
              [ESearchFilters.DELIVERY]: undefined,
              [ESearchFilters.DELIVERY_STATIONARY]: undefined,
            },
            false,
            dryRun,
            false,
            undefined,
            true,
          ), // ignoreExistingQueryParams = true to clear all other filters
        );
      }

      if (onModeSelect) {
        onModeSelect();
      }
    },
    [dispatch, queryParams, formState, onModeSelect],
  );

  return { handleModeSelect };
};
